import { Component, OnInit, OnDestroy, signal, computed, inject, effect, Output, EventEmitter, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { OrganisationService } from 'src/app/services/organisation.service';
import { InventoryService } from 'src/app/services/inventory.service';
import { ProductService } from 'src/app/services/product.service';
import { NavStateService } from 'src/app/services/nav-state.service';
import { UserHelperService } from '../shared/user-helper.service';
import { FormBuilder, Validators } from '@angular/forms';
import { Subscription, interval, Observable } from 'rxjs';
import { take, switchMap } from 'rxjs/operators';
import { isEqual } from 'lodash';
declare var $: any;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit, OnDestroy {

  @Output() headerHeightChanged = new EventEmitter<void>();

  // Services using modern dependency injection
  private router = inject(Router);
  private authService = inject(AuthService);
  private utilService = inject(UtilService);
  private translate = inject(TranslateService);
  private organisationService = inject(OrganisationService);
  private formBuilder = inject(FormBuilder);
  private inventoryService = inject(InventoryService);
  private productService = inject(ProductService);
  private navStateService = inject(NavStateService);
  private userHelper = inject(UserHelperService);

  /** Shared with product list via InventoryService (avoids /ongoing Subject race). */
  readonly inventoryOngoing = this.inventoryService.inventoryOngoing;

  constructor(private host: ElementRef<HTMLElement>) {
    // Watch for inventory/revision status changes and notify parent
    effect(() => {
      this.inventoryOngoing();
      this.revisionOngoing();
      setTimeout(() => {
        this.headerHeightChanged.emit();
      }, 100);
    });
  }
  // Signals for reactive state
  searchList = signal<any[]>([]);
  searchText = signal<string>('');
  currentUser = signal<any>(null);
  isSettings = signal<boolean>(false);
  selectedOrganisation = signal<any>(null);
  selectedDepartment = signal<any>(null);
  toggle = signal<boolean>(true);
  allDepartments = signal<any[]>([]);
  organisation = signal<any>(null);
  selectedDepartmentName = signal<string>('');
  departmentSearch = signal<string>('');
  loginDepartment = signal<any>(null);
  loginTreeList = signal<any[]>([]);
  isLoading = signal<boolean>(false);
  revisionOngoing = signal<boolean>(false);
  isDepartmentDropdownOpen = signal(false);
  /** Desktop: which department nodes are expanded (level 1 expanded by default) */
  expandedDepartmentIds = signal<Set<string>>(new Set());

  listAlldepartments: any;

  /** Visible tree nodes for desktop expandable list (root first, then children; depends on organisation + expandedDepartmentIds) */
  visibleDepartmentNodes = computed(() => {
    const org = this.organisation();
    if (!org) return [];
    const expanded = this.expandedDepartmentIds();
    const rootExpanded = expanded.has(String(org.id));
    const rootItem = {
      org,
      name: '\u2022 ' + org.name,
      level: 0,
      hasChildren: Array.isArray(org.orgs) && org.orgs.length > 0,
      isExpanded: rootExpanded,
    };
    const children = org.orgs?.length
      ? this.getVisibleDepartmentNodes(org.orgs, expanded, 1, rootExpanded)
      : [];
    return [rootItem, ...children];
  });

  // Computed values
  isLocalUser = computed(() => {
    const user = this.currentUser();
    return user && (user.is_local_user === 1 || user.is_local_admin === 1 || ( user.is_guest === 1 && user.guest_show_subdepartments=== 1 ) );
  });

  isGuest = computed(() => {
    const user = this.currentUser();
    return user && user.is_guest === 1 && user.guest_show_subdepartments!== 1;
  });

  isSuperUser = computed(() => {
    const user = this.currentUser();
    return user && user.is_super_user === 1;
  });

  isKoncernMode = computed(() => this.userHelper.isGroupAdminInKoncernMode(this.currentUser()));

  // Subscriptions
  private departmentChangedSubscription: Subscription | null = null;
  private pollingSubscription: Subscription | null = null;
  private revisionStatusSubscription: Subscription | null = null;

  async ngOnInit(): Promise<void> {
    this.revisionStatusSubscription?.unsubscribe();
    this.searchList.set([]);
    this.selectedDepartmentName.set('');
    this.isLoading.set(true);

    // Load current user
    const data = localStorage.getItem('currentUser');
    if (!data) {
      this.logout();
      return;
    }

    this.currentUser.set(JSON.parse(data));

    // Load organization data
    const rootOrg = JSON.parse(localStorage.getItem('rootOrganisation') || 'null');
    this.organisation.set(rootOrg);
    this.selectedOrganisation.set(rootOrg);

    // Load selected department
    const selectedDept = localStorage.getItem('selectedDepartment');
    if (!selectedDept) {
      const dept = { ...rootOrg, level: 1 };
      this.selectedDepartment.set(dept);
    } else {
      const dept = JSON.parse(selectedDept);
      dept.level = 2;
      this.selectedDepartment.set(dept || { ...rootOrg, level: 1 });
    }

    // Setup local user departments
    this.listAlldepartments = await this.userHelper.getAllDepartments(this.organisation());
    this.setupLocalUserDepartments();


    this.setOrgTree(this.organisation());
    this.initializeDepartments();
    this.getInventoryStatus();
    this.getRevisionStatus();

    // Subscribe to department changes
    this.departmentChangedSubscription = this.organisationService.departmentChanged.subscribe(() => {
      this.handleDepartmentChange();
    });

    this.revisionStatusSubscription = this.productService.getRevisionStatusListener().subscribe((status) => {
      if (!status) {
        this.refreshRevisionOngoingFromOrg();
        return;
      }
      const payload = status?.organisation ?? status;
      this.revisionOngoing.set(
        payload?.revision_ongoing === true ||
          payload?.revision_ongoing === 1 ||
          payload?.revision_ongoing === '1'
      );
    });

    setTimeout(() => {
      localStorage.removeItem("set");
    }, 2000);

    this.startPollingDepartments();
  }

  ngOnDestroy(): void {
    this.pollingSubscription?.unsubscribe();
    this.departmentChangedSubscription?.unsubscribe();
    this.revisionStatusSubscription?.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.host.nativeElement.contains(event.target as Node);
    if (!clickedInside) {
      this.isDepartmentDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.isDepartmentDropdownOpen.set(false);
    }
  }

  private setupLocalUserDepartments(): void {
    const user = this.currentUser();
    if (!user) return;

    // Handle local users, local admins, and guest users with guest_show_subdepartments === 1
    // All use is_local_user_department to store allowed departments
    if (this.isLocalUser()) {
      user.LocalUserDepartments = [];

      if (user.department?.trim() === "" && user.is_local_user_department?.trim() === "") {
        user.LocalUserDepartments.push(user.root_organisation_id?.trim());
      } else if (user.is_guest === 1 && user.guest_show_subdepartments=== 1 && user.department?.trim() === user.root_organisation_id?.trim()) {
        this.listAlldepartments.forEach(department => {
            user.LocalUserDepartments.push(department.id);
          });
      } else {
        // Split by comma (handles both ',' and ', ' formats) and trim each value
        const departmentArrayTemp = user.is_local_user_department?.split(',').map(d => d.trim()) || [];
        const rootOrgId = this.organisation()?.id;
        const shouldShowSubdepartments = this.utilService.resolveShowSubdepartments();

        for (const value of departmentArrayTemp) {
          if (value) {
            user.LocalUserDepartments.push(value);

            // If this is the root organization and showSubdepartments is enabled, add all sub-departments
            if ((value === String(rootOrgId) || value === rootOrgId) && shouldShowSubdepartments) {
              const subDepts = this.utilService.getAllSubdepartments(
                this.organisation(),
                true
              );
              if (subDepts && Array.isArray(subDepts)) {
                subDepts.forEach(subId => {
                  const subIdStr = String(subId);
                  if (!user.LocalUserDepartments.includes(subIdStr)) {
                    user.LocalUserDepartments.push(subIdStr);
                  }
                });
              }
            } else if (value !== String(rootOrgId) && value !== rootOrgId && shouldShowSubdepartments) {
              // For non-root departments, also add their sub-departments if showSubdepartments is enabled
              const deptObj = this.utilService.getDepartmentObjectFromId(value, this.organisation());
              if (deptObj) {
                const subDepts = this.utilService.getAllSubdepartments(deptObj, true);
                if (subDepts && Array.isArray(subDepts)) {
                  subDepts.forEach(subId => {
                    const subIdStr = String(subId);
                    if (!user.LocalUserDepartments.includes(subIdStr)) {
                      user.LocalUserDepartments.push(subIdStr);
                    }
                  });
                }
              }
            }
          }
        }
      }
      // This is in case it isnt found earlier (which can happen on leafes without children)
      if (user.is_guest === 1 && user.guest_show_subdepartments=== 1 && user.LocalUserDepartments.length === 0) {
        user.LocalUserDepartments.push(user.department?.trim());
      }

    } else {
      user.isLocalUser = "";
      user.LocalUserDepartments = [];
    }

    this.currentUser.set(user);
  }

  private initializeDepartments(): void {
    const org = this.organisation();
    if (!org) return;

    const deps = [{ org, name: '\u2022 ' + org.name, level: 1 }];

    const storedDepts = localStorage.getItem('allDepartments');
    if (storedDepts) {
      this.allDepartments.set(JSON.parse(storedDepts));
      this.isLoading.set(false);
    } else {
      // Add deps first to allDepartments
      this.allDepartments.set(deps);
      this.iterateOrganisation(org.orgs, 1);
      localStorage.setItem('allDepartments', JSON.stringify(this.allDepartments()));
      this.isLoading.set(false);
    }
  }

  private handleDepartmentChange(): void {
    const rootOrg = JSON.parse(localStorage.getItem('rootOrganisation') || 'null');
    this.organisation.set(rootOrg);
    this.selectedOrganisation.set(rootOrg);

    const selectedDept = localStorage.getItem('selectedDepartment');
    if (!selectedDept) {
      this.selectedDepartment.set(rootOrg);
    } else {
      const dept = JSON.parse(selectedDept);
      this.selectedDepartment.set(dept || rootOrg);
    }

    this.ngOnInit();
    this.departmentChangedSubscription?.unsubscribe();
  }

  startPollingDepartments() {
    this.pollingSubscription?.unsubscribe();

    this.pollingSubscription = interval(300000).pipe( // 5 minutes
      switchMap(() => {
        const org = this.organisation();
        if (!org?.id) return [];

        this.organisationService.getOrganisation(org.id);
        return this.organisationService.getOrganisationOrgDataListener().pipe(take(1));
      })
    ).subscribe({
      next: (organisation) => {
        if (!organisation) return;

        this.setOrgTree(organisation);
        let tempAllDepartments = [];
        tempAllDepartments.push({ org: organisation, name: '\u2022 ' + organisation.name, level: 1 });
        tempAllDepartments = this.iterateOrganisationTemp(organisation.orgs, 1, tempAllDepartments);

        if (organisation && this.organisation() && !isEqual(this.allDepartments(), tempAllDepartments)) {
          localStorage.setItem('selectedOrganisation', JSON.stringify(organisation));
          localStorage.setItem('rootOrganisation', JSON.stringify(organisation));
          this.organisation.set(organisation);
          this.allDepartments.set(tempAllDepartments);
          localStorage.setItem('allDepartments', JSON.stringify(tempAllDepartments));
        }
      },
      error: (error) => {
        console.error('Error fetching organisation:', error);
      }
    });
  }

  setOrgTree(organisation: any): void {
    if (!organisation?.orgs?.length) return;

    for (let i = 0; i < organisation.orgs.length; i++) {
      organisation.orgs[i].disabled = true;

      const user = this.currentUser();
      if (this.isLocalUser() || this.isGuest()) {
        for (let j = 0; j < user.LocalUserDepartments.length; j++) {
          if (organisation.orgs[i].id === user.LocalUserDepartments[j]) {
            organisation.orgs[i].disabled = false;
          }
        }
      } else {
        organisation.orgs[i].disabled = false;
      }

      if (organisation.orgs[i].orgs?.length > 0) {
        this.setOrgTree(organisation.orgs[i]);
      }
    }
  }

  getInventoryStatus(): void {
    this.inventoryService.refreshOngoingStatus(this.selectedDepartment()?.id);
  }

  getRevisionStatus(): void {
    this.refreshRevisionOngoingFromOrg();

    const orgId = this.organisation()?.id ?? this.selectedOrganisation()?.id;
    if (!orgId) return;

    this.productService.getOrganisationRevisionStatus(orgId).pipe(take(1)).subscribe({
      next: (status) => {
        if (!status) {
          this.refreshRevisionOngoingFromOrg();
          return;
        }
        this.productService.applyRevisionOrgState(status?.organisation ?? status);
        this.refreshRevisionOngoingFromOrg();
      },
      error: (error) => {
        console.log('Error: ', error);
        this.refreshRevisionOngoingFromOrg();
      },
    });
  }

  private refreshRevisionOngoingFromOrg(): void {
    const org =
      this.organisation() ??
      this.selectedOrganisation() ??
      JSON.parse(localStorage.getItem('rootOrganisation') || 'null');
    this.revisionOngoing.set(
      org?.revision_ongoing === true ||
        org?.revision_ongoing === 1 ||
        org?.revision_ongoing === '1'
    );
  }

  onSearchChange(searchValue: string): void {
    this.allDepartments.set([]);
    this.allDepartments.set([{org: this.organisation(), name: this.organisation().name, level: 1}]);
    this.iterateOrganisation(this.organisation().orgs, 1);

    if (searchValue !== "")
    {
        var currentDepartments = [];
        currentDepartments = this.allDepartments().filter(item => item.org.name.toLowerCase().includes(searchValue.toLowerCase()));

        if (currentDepartments.length > 0) {
          this.allDepartments.set([]);

          currentDepartments.forEach(currentDepartment => {
            const current = this.allDepartments();
            current.push({org: currentDepartment.org, name: currentDepartment.name, level: 1});
            this.allDepartments.set(current);
            this.iterateOrganisation(currentDepartment.org.orgs, 1);
          });
        }

      // Remove duplicates ( in name and id not level )
      const filtered = this.allDepartments().reduce((acc, department) => {
        const existing = acc.find(d => d.name === department.name && d.org.id === department.org.id);
        if (!existing || department.level > existing.level) {
          return acc.filter(d => !(d.name === department.name && d.org.id === department.org.id)).concat(department);
        }
        return acc;
      }, [] as any[]);

      this.allDepartments.set(filtered);
    }
  }

  addMessage(department: any): void {
    localStorage.setItem('departmentchanged','set');
    const user = this.currentUser();
    if (user.is_local_user == 1 || user.is_local_admin == 1 || user.is_guest == 1) {
      for (let j = 0; j < user.LocalUserDepartments.length; j++) {
        if (department.id == user.LocalUserDepartments[j]) {
          this.selectedDepartment.set(department);
          localStorage.setItem('selectedDepartment', JSON.stringify(department));
          this.organisationService.departmentChanged.next(department);
          this.isDepartmentDropdownOpen.set(false);
        }
      }
    } else {
      this.selectedDepartment.set(department);
      localStorage.setItem('selectedDepartment', JSON.stringify(department));
      this.organisationService.departmentChanged.next(department);
      this.isDepartmentDropdownOpen.set(false);
    }
  }

  removeSubscriptions(orgs: any[]): void {
    Object.keys(orgs)
        .filter(key => key.startsWith('subscription'))
        .forEach(key => delete orgs[key]);
  }

  removeSubscriptionsTemp(orgs: any[], deps: any[]): void {
    Object.keys(orgs)
        .filter(key => key.startsWith('subscription'))
        .forEach(key => delete orgs[key]);
  }

  iterateOrganisation(orgs: any[], depth: number): void {
    orgs.forEach(org => {
      if (org.disabled == false)
      {
        this.removeSubscriptions(org);
        const current = this.allDepartments();
        current.push({org: org, name: '\u2022 ' + org.name, level: depth+1});
        this.allDepartments.set(current);
      }

      if (org.orgs.length > 0) {
        this.iterateOrganisation(org.orgs, depth + 1);
      }
    });
  }

  iterateOrganisationTemp(orgs: any[], depth: number, deps: any[]): any[] {
    orgs.forEach(org => {
      if (org.disabled == false)
      {
        this.removeSubscriptions(org);
        deps.push({org: org, name: '\u2022 ' + org.name, level: depth+1});
      }

      if (org.orgs.length > 0) {
        this.iterateOrganisationTemp(org.orgs, depth + 1, deps);
      }
    });
    return deps;
  }

  departFullName(department: any): any[] {
    let deps = this.utilService.departFullNameHeader(department, this.listAlldepartments);
    return Array.isArray(deps) ? deps : [];
  }

  getDepartmentPathString(department: any): string {
    const deps = this.departFullName(department);
    if (deps.length > 1) {
      return deps.slice(0, -1).map((d: any) => d.name).join(' > ');
    }
    return '';
  }

  navigateHome(): void {
    const user = this.currentUser();
    if(user && user.is_guest==1){
      return;
    }

    this.navStateService.changeNavigationUrl('/dashboard');
  }

  logout(): void {
    this.authService.logout();
  }

  gotoCustomerCard(): void {
    this.router.navigate(["/organisation", this.organisation().id]).then(() => {
      window.location.reload();
    });
  }

  gotoDepartment(dep: any): void {
    const user = this.currentUser();
    if (user.is_guest == 1) {return;}
    let org;
    this.allDepartments().forEach(department => {
      if (department.org.id == dep.id) {
        org = department.org;
      }
    });
    this.addMessage(org)
  }

  getTreeDifferences(orgs1: any[], orgs2: any[]): string {
    const diff = [];
    const maxLength = Math.max(orgs1.length, orgs2.length);
    for (let i = 0; i < maxLength; i++) {
      if (!isEqual(orgs1[i], orgs2[i])) {
        diff.push(`Difference at index ${i}: ${JSON.stringify(orgs1[i])} !== ${JSON.stringify(orgs2[i])}`);
      }
    }
    return diff.join('\n');
  }

  toggleDepartmentDropdown() {
    const willOpen = !this.isDepartmentDropdownOpen();
    this.isDepartmentDropdownOpen.set(willOpen);
    if (willOpen) {
      this.expandFirstLevelOnly();
    }
  }

  /** Expand root and level 1 (root’s direct children) for desktop tree; root is always included so it can be selected */
  expandFirstLevelOnly(): void {
    const org = this.organisation();
    const ids = new Set<string>();
    if (org?.id) ids.add(String(org.id));

    if (org?.orgs?.length) {
      if (this.isLocalUser() || this.isGuest()) {
        // Local admins should see enabled departments and their subdepartments,
        // even when intermediate ancestors are disabled and not rendered.
        this.collectExpandableEnabledDepartmentIds(org.orgs, ids);
      } else {
        // Non-local users keep the old behavior: expand only level 1 nodes.
        for (const o of org.orgs) {
          if (o.disabled !== true) ids.add(String(o.id));
        }
      }
    }
    this.expandedDepartmentIds.set(ids);
  }

  toggleDepartmentExpand(id: string): void {
    const set = new Set(this.expandedDepartmentIds());
    if (set.has(id)) set.delete(id);
    else set.add(id);
    this.expandedDepartmentIds.set(set);
  }

  /** Build flat list of visible tree nodes (for desktop expandable list) */
  getVisibleDepartmentNodes(
    orgs: any[] | undefined,
    expandedIds: Set<string>,
    level: number,
    parentExpanded: boolean
  ): any[] {
    if (!orgs?.length) return [];
    const result: any[] = [];
    for (const org of orgs) {
      const isAllowed = org.disabled !== true;

      // Render only enabled nodes, but still traverse through disabled ancestors
      // so enabled descendants (e.g. dep2) can be shown.
      if (parentExpanded && isAllowed) {
        result.push({
          org,
          name: '\u2022 ' + org.name,
          level,
          hasChildren: Array.isArray(org.orgs) && org.orgs.length > 0,
          isExpanded: expandedIds.has(String(org.id)),
        });
      }

      const canTraverseChildren =
        parentExpanded &&
        Array.isArray(org.orgs) &&
        org.orgs.length > 0 &&
        // If this node is disabled we still traverse to find enabled children.
        (!isAllowed || expandedIds.has(String(org.id)));

      if (canTraverseChildren) {
        result.push(...this.getVisibleDepartmentNodes(org.orgs, expandedIds, level + 1, true));
      }
    }
    return result;
  }

  private collectExpandableEnabledDepartmentIds(orgs: any[], ids: Set<string>): void {
    for (const org of orgs) {
      if (org?.orgs?.length && org.disabled !== true) {
        ids.add(String(org.id));
      }
      if (Array.isArray(org?.orgs) && org.orgs.length > 0) {
        this.collectExpandableEnabledDepartmentIds(org.orgs, ids);
      }
    }
  }
}

